/** @format */

import {useState} from 'react';

import {Box, Typography} from '@mui/material';
import {Construction, DirectionsRun} from '@mui/icons-material';
import {Trans, useTranslation} from 'react-i18next';

import WidgetSwitcherButton from 'components/PropertyWidget/components/WidgetSwitcherButton';

import {useMixpanelContext} from 'contexts/MixpanelContext';
import {useCompanyContext} from 'contexts/CompanyContext';

import {WIDGETS_LIST} from '../../constants/widgets';
import {ChartIds} from '../../models/chartIds';
import {useStubLayerContext} from '../../contexts/StubLayerContext';
import getCategoryIcon from '../../helpers/getCategoryIcon';
import {StubWidgetRoot as Root, VoteButton, stubWidgetClasses as classes} from './style';

interface Props {
  chartId: ChartIds;
  color: string;
  selected: boolean;
  onSwitcherClick: () => void;
}

const StubWidget = ({chartId, color, selected, onSwitcherClick}: Props) => {
  const {t} = useTranslation();
  const {getLayerData, vote} = useStubLayerContext();
  const {mixpanel} = useMixpanelContext();
  const {currentCompany} = useCompanyContext();
  const layerData = getLayerData(chartId);
  const widgetData = WIDGETS_LIST[chartId];
  const categoryIcon = getCategoryIcon(chartId);

  const [isVoting, setIsVoting] = useState(false);

  const totalVotes = layerData?.totalVotes ?? 0;
  const hasVoted = layerData?.hasVoted ?? false;

  const handleVote = async () => {
    setIsVoting(true);
    mixpanel.track('vote_for_stub_layer', {
      project_uuid: currentCompany?.uuid,
      layer_id: chartId,
    });
    try {
      await vote(chartId);
    } finally {
      setIsVoting(false);
    }
  };

  return (
    <Root color={color}>
      {/* Header row */}
      <Box className={classes.headerRow} data-testid='header-row'>
        <Box className={classes.headerLeftGroup}>
          {categoryIcon ? (
            <Box className={classes.categoryIconsBox}>
              <Typography className={classes.iconLabel}>{widgetData.label}</Typography>
              {categoryIcon}
            </Box>
          ) : (
            <Typography className={classes.titleText}>{widgetData.label}</Typography>
          )}
          <Box className={classes.badge}>
            <Construction className={classes.badgeIcon} />
            <Typography className={classes.badgeText}>
              <Trans>В разработке</Trans>
            </Typography>
          </Box>
        </Box>
        <Box sx={{pl: 1}}>
          <WidgetSwitcherButton color={color} onClick={onSwitcherClick} selected={selected} />
        </Box>
      </Box>

      {/* Body row */}
      <Box className={classes.bodyRow} data-testid='body-row'>
        <Box className={classes.infoColumn}>
          <Typography className={classes.acceleratedLabel}>
            <Trans>Уже ускорили</Trans>
          </Typography>
          <Typography className={classes.votesText}>
            {t('plurals.stub_votes_times', {count: totalVotes})}
          </Typography>
        </Box>

        {hasVoted ? (
          <>
            <Box className={classes.divider} />
            <Box className={classes.votedBlock}>
              <DirectionsRun sx={{fontSize: 28, fontWeight: 300}} />
              <Typography className={classes.votedText}>
                <Trans>Вы ускорили</Trans>
                <br />
                <Trans>разработку</Trans>
              </Typography>
            </Box>
          </>
        ) : (
          <VoteButton onClick={handleVote} disabled={isVoting}>
            <DirectionsRun sx={{fontSize: 16}} />
            <Trans>Ускорить добавление</Trans>
          </VoteButton>
        )}
      </Box>
    </Root>
  );
};

export default StubWidget;
